import styled from "styled-components";
import { Box } from "../../components/Box";
import { Image } from "../../components/Image";
import { checksStatusIconsMap } from "./helper";

/**
 * One tab per check, in backend order. Wraps to further lines as needed and
 * never scrolls or collapses into an overflow menu — a hidden tab is a hidden
 * check, and an officer must be able to see every check that ran without
 * interacting with anything.
 *
 * Deliberately not built on CustomTabs: that component mounts every tab's
 * content up front and hides the inactive ones with display:none, and each
 * check component fetches its own data on mount. With up to eleven checks
 * that's eleven requests on page load instead of one. The strip and panel are
 * driven separately here so only the selected check ever mounts.
 */

const TabButton = styled.button<{ isActive: boolean }>`
  ${({ theme: { colors, fontFamily, fontSizes, fontWeights, space }, isActive }) => `
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    font-family: ${fontFamily};
    font-size: ${fontSizes[1]};
    font-weight: ${isActive ? fontWeights.semibold : fontWeights.medium};
    color: ${isActive ? colors.gray[80] : colors.gray[50]};
    background: none;
    border: none;
    border-bottom: 3px solid ${isActive ? colors.blue.primary : "transparent"};
    padding: ${space[2]} 0;
    margin-right: ${space[4]};
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease, border-bottom-color 0.15s ease;

    &:hover {
      color: ${colors.gray[70]};
    }

    &:focus {
      outline: none;
    }
  `}
`;

const Strip = styled(Box)`
  ${({ theme: { colors } }) => `
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-bottom: 1px solid ${colors.gray[20]};
  `}
`;

type CheckTabStripEntry = {
  id: number;
  name: string;
  title: string;
  status: { status: string };
};

type Props = {
  checks: CheckTabStripEntry[];
  selectedId: number | null;
  onSelect: (id: number) => void;
};

export const CheckTabStrip = ({ checks, selectedId, onSelect }: Props) => (
  <Strip mb={3}>
    {checks.map((check) => (
      <TabButton
        key={check.id}
        type="button"
        isActive={check.id === selectedId}
        onClick={() => onSelect(check.id)}
      >
        <Image
          src={checksStatusIconsMap[check.status.status]}
          alt=""
          height="16px"
          width="16px"
        />
        {check.title}
      </TabButton>
    ))}
  </Strip>
);
